import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCircle2, Save, Instagram, Youtube, Globe, Music2, ExternalLink, ShieldCheck, KeyRound, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getMyArtist, linkMyArtist, updateMyArtistBio, getMarketingEligibility } from "@/lib/afmApi";
import { ArtistPicker } from "@/components/ArtistPicker";
import { PasswordResetFlow } from "@/components/PasswordResetFlow";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loading, SectionTitle } from "@/components/common";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
const MAX = 600;

export default function ArtistProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(undefined);
  const [pick, setPick] = useState([]);
  const [f, setF] = useState({ bio: "", instagram: "", tiktok: "", youtube: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [credit, setCredit] = useState(0);

  useEffect(() => {
    getMyArtist(user.email).then((a) => {
      setArtist(a || null);
      if (a) { setPick([a]); setF({ bio: a.bio || "", instagram: a.instagram || "", tiktok: a.tiktok || "", youtube: a.youtube || "", website: a.website || "" }); }
    });
    getMarketingEligibility(user.id).then((d) => setCredit(d?.welcome_credit_usd || 0)).catch(() => setCredit(0));
  }, [user.email, user.id]);

  const link = async (arr) => {
    setPick(arr);
    if (!arr[0]) return;
    try {
      const a = await linkMyArtist({ email: user.email, name: arr[0].name, spotify_id: arr[0].spotify_id, spotify_url: arr[0].spotify_url, apple_id: arr[0].apple_id, apple_url: arr[0].apple_url, image: arr[0].image, ...f });
      setArtist(a); toast.success(`Perfil vinculado: ${a.name}`);
    } catch { toast.error("No se pudo vincular"); }
  };
  const save = async () => {
    setBusy(true);
    try { const a = await updateMyArtistBio({ email: user.email, ...f }); setArtist(a); toast.success("Perfil guardado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    setBusy(false);
  };

  if (artist === undefined) return <Loading />;
  return (
    <div className="space-y-6" data-testid="artist-profile-page">
      <SectionTitle icon={UserCircle2} title="Mi Perfil de Artista" desc="Tu biografía y redes se muestran en Always Fresh Finds cuando los fans tocan tu nombre." />

      {credit > 0 && (
        <div data-testid="profile-welcome-credit" className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-5 dark:border-emerald-500/30 dark:from-emerald-500/10 dark:to-[#171717]">
          <div className="flex items-start gap-3">
            <div className="rounded-xl border border-emerald-200 bg-white p-2.5 text-emerald-600"><Gift className="h-6 w-6" /></div>
            <div>
              <h3 className="font-serif text-lg font-bold text-black">Tienes ${credit} de regalo 🎉</h3>
              <p className="mt-0.5 text-sm text-slate-600">Bono de bienvenida a Always Fresh. Se descuenta automáticamente en tu <strong>primera campaña</strong> para promocionar tu primer tema.</p>
            </div>
          </div>
          <button data-testid="profile-credit-cta" onClick={() => navigate("/predictive")} className="shrink-0 rounded-lg bg-[#AC3333] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#FF0505]">Promocionar mi tema →</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Perfil vinculado</div>
        <div className="mt-3">
          <ArtistPicker single value={pick} onChange={link} testId="profile-artist" userId={user.id} ph="Busca tu perfil en Spotify / Apple Music…" />
        </div>
        {artist && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {artist.spotify_url ? <a href={artist.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-[#1DB954]/15 px-2 py-1 font-semibold text-[#15803d]">Spotify <ExternalLink className="h-3 w-3" /></a> : <span className="rounded-md bg-amber-100 px-2 py-1 font-semibold text-amber-800">Sin perfil de Spotify — búscalo arriba para vincularlo</span>}
            {artist.apple_url && <a href={artist.apple_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md bg-black px-2 py-1 font-semibold text-white">Apple Music <ExternalLink className="h-3 w-3" /></a>}
          </div>
        )}
      </div>

      <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${!artist ? "pointer-events-none opacity-50" : ""}`}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Biografía corta</div>
        <textarea data-testid="profile-bio" value={f.bio} maxLength={MAX} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={5} placeholder="Cuéntale a tus fans quién eres, tu género y de dónde vienes…" className={`${inputCls} mt-3 resize-none`} />
        <div className="mt-1 text-right text-[11px] text-slate-400" data-testid="profile-bio-count">{f.bio.length}/{MAX}</div>

        <div className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Redes</div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {[["instagram", "Instagram", Instagram, "https://instagram.com/tuusuario"], ["tiktok", "TikTok", Music2, "https://tiktok.com/@tuusuario"], ["youtube", "YouTube", Youtube, "https://youtube.com/@tucanal"], ["website", "Sitio web", Globe, "https://…"]].map(([k, l, Icon, ph]) => (
            <label key={k} className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Icon className="h-3.5 w-3.5" /> {l}</span>
              <input data-testid={`profile-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className={inputCls} />
            </label>
          ))}
        </div>
        <button data-testid="profile-save" disabled={busy || !artist} onClick={save} className="mt-5 flex items-center gap-1.5 rounded-lg bg-black px-5 py-2.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Guardar perfil</button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="security-card">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Seguridad</div>
        <p className="mt-2 text-sm text-slate-600">Cambia tu contraseña de acceso. Te enviaremos un código a <span className="font-semibold text-black">{user.email}</span> para confirmar el cambio.</p>
        <button data-testid="change-password-btn" onClick={() => setPwOpen(true)} className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-5 py-2.5 text-xs font-semibold text-black transition-colors hover:border-black"><KeyRound className="h-3.5 w-3.5" /> Cambiar contraseña</button>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Cambiar contraseña</DialogTitle>
            <DialogDescription>Enviaremos un código a tu correo para confirmar el cambio.</DialogDescription>
          </DialogHeader>
          <PasswordResetFlow defaultEmail={user.email} lockEmail onDone={() => setPwOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
